// Opcodes, FRAME section ids, entity kinds, flag bits, NOTICE and ERROR codes (protocol §5).

type ValueOf<T> = T[keyof T];

/** Client → server opcodes (protocol §5.1). */
export const C2S = {
  HELLO: 0x01,
  PLAY: 0x02,
  INPUT: 0x03,
  SPLIT: 0x04,
  EJECT: 0x05,
  SPECIAL: 0x06,
  SPECTATE: 0x07,
  OPTIONS: 0x08,
  PING: 0x09,
  /** Money rooms: start the cash-out hold (money-mode §4). No payload. */
  CASH_OUT_START: 0x0a,
  /** Money rooms: release the cash-out key (money-mode §4). No payload. */
  CASH_OUT_CANCEL: 0x0b,
  /**
   * Arena rooms (WELCOME arena block): the steering heading, replacing INPUT (protocol §5.1).
   * `u16` heading (65,536 steps per turn), `u16` seq (wraps). Ignored in cell rooms.
   */
  ARENA_INPUT: 0x0c,
} as const;
export type C2SOp = ValueOf<typeof C2S>;

/** Server → client opcodes (protocol §5.2). */
export const S2C = {
  WELCOME: 0x80,
  FRAME: 0x81,
  SPAWNED: 0x82,
  DEATH: 0x83,
  PONG: 0x84,
  NOTICE: 0x85,
  PARTY: 0x86,
  MATCH_RESULT: 0x87,
  /** Money rooms: a player's cash-out ring started, progressed or stopped (money-mode §4). */
  CASH_OUT_STATE: 0x88,
  /** Money rooms: your cash-out completed; value paid and the life summary (money-mode §4). */
  CASH_OUT_DONE: 0x89,
  ERROR: 0x8f,
} as const;
export type S2COp = ValueOf<typeof S2C>;

/** FRAME section ids, in the ascending order they must appear (protocol §5.4). */
export const SECTION = {
  PLAYERS: 0x01,
  ADD: 0x02,
  MOVE: 0x03,
  EATEN: 0x04,
  REMOVE: 0x05,
  OWN: 0x06,
  CAMERA: 0x07,
  LEADERBOARD: 0x08,
  TEAMS: 0x09,
  MINIMAP: 0x0a,
  MATCH: 0x0b,
  ROOM: 0x0c,
  /** Money and practice (arena) rooms: label price and per-player bag in lamports. */
  MONEY: 0x0d,
  // 0x0E was BACKSTAB (the agar.io money game's backstabs); retired with it in lane X
  // (2026-10-31). Never reuse it: an old client would misread the section.
  // Arena rooms only (WELCOME arena block; docs/territory-arena.md §11, protocol §5.4). An arena
  // FRAME uses PLAYERS (identity), CAMERA (observers), LEADERBOARD (with the land-percent
  // trailer), ROOM and MONEY from above, then these; never ADD, MOVE, EATEN, REMOVE, OWN, TEAMS,
  // MINIMAP or MATCH.
  /** Land chunks: RLE per 16 × 16 chunk; a snapshot on join and large view changes. */
  ARENA_LAND: 0x10,
  /** Heads in view: position, heading, flags, cash-out progress. */
  ARENA_HEADS: 0x11,
  /** Trail appends, resets and clears. */
  ARENA_TRAILS: 0x12,
  /** Drops added (with value) and removed (with the collector). */
  ARENA_DROPS: 0x13,
  /** Room-wide events: captures, deaths, spills, collections, exits (kill feed, flashes, sounds). */
  ARENA_EVENTS: 0x14,
  /** Your own life: land cells, kills, the last applied input seq. */
  ARENA_OWN: 0x15,
  /** The downsampled ownership minimap and the top heads, every 25 ticks. */
  ARENA_MINIMAP: 0x16,
} as const;
export type SectionId = ValueOf<typeof SECTION>;

/** Entity kinds in ADD records (protocol §5.4; architecture §3). */
export const KIND = {
  CELL: 0,
  FOOD: 1,
  EJECTED: 2,
  VIRUS: 3,
  SPAWNER: 4,
} as const;
export type Kind = ValueOf<typeof KIND>;

export function isKind(v: number): v is Kind {
  return v === 0 || v === 1 || v === 2 || v === 3 || v === 4;
}

/** Which simulation a room runs (WELCOME trailer `u8 sim`, protocol §5.3). */
export const WELCOME_SIM = {
  /** The agar.io cell World (classic FFA). Never written: no trailer means cells. */
  CELLS: 0,
  /** The territory arena; the arena block follows. */
  TERRITORY: 1,
} as const;
export type WelcomeSimCode = ValueOf<typeof WELCOME_SIM>;

/** WELCOME arena block `shape`. */
export const ARENA_SHAPE_CODE = {
  SQUARE: 0,
  CIRCLE: 1,
} as const;

/** ARENA_HEADS record flag bits. A CASHING_OUT record carries `u16` progress × 10000. */
export const ARENA_HEAD_FLAG = {
  /** Capture shield (spec §6): the land cannot be captured; drawn as a dashed outline. */
  SHIELDED: 1 << 0,
  /** A cash-out hold is armed (the ring and `$` marker; progress follows). */
  CASHING_OUT: 1 << 1,
  /** Frozen in place (disconnected, inside the grace; spec §9). */
  FROZEN: 1 << 2,
  /** Forced exit: the server is driving (plug icon; spec §9). */
  AUTOPILOT: 1 << 3,
  /** Own head only: an enemy head stands on your land (minimap rim, optional sound; S-5). */
  ALARM: 1 << 4,
} as const;

/** ARENA_TRAILS record op. */
export const ARENA_TRAIL_OP = {
  /** `vu n`, then n 2-bit steps continuing from the last cell this client holds for the trail. */
  APPEND: 0,
  /** `vu startCell`, `vu n`, n 2-bit steps: replaces the client's copy (first sight, resync). */
  RESET: 1,
  /** The trail is gone (capture, death, exit). */
  CLEAR: 2,
} as const;
export type ArenaTrailOp = ValueOf<typeof ARENA_TRAIL_OP>;

/**
 * 2-bit trail step codes (4-connected supercover: each trail cell is a neighbour of the last),
 * packed four per byte, first step in the low bits.
 */
export const ARENA_STEP = {
  RIGHT: 0,
  DOWN: 1,
  LEFT: 2,
  UP: 3,
} as const;

/** ARENA_LAND section flag bits. */
export const ARENA_LAND_FLAG = {
  /** Forget every cached chunk first (join, respawn, large view change). */
  SNAPSHOT: 1 << 0,
} as const;

/** ARENA_EVENTS record type. */
export const ARENA_EVENT = {
  /** `vu playerId`, `vu gainedCells`, `vu cellIndex` (a cell inside the capture, for sound pan). */
  CAPTURE: 1,
  /** `vu victimId`, `vu killerId` (0 = none), `u8` cause code, `u8` flags (bit0 atWall), `vl` value. */
  DEATH: 2,
  /** `vu victimId`, `vu dropCount`, `vl` value (spilled across the drops). */
  SPILL: 3,
  /** `vu playerId`, `vu dropId`, `vl` value. */
  COLLECT: 4,
  /** `vu playerId`, `u8` exit code (ARENA_EXIT_CODE), `vl` value paid. */
  EXIT: 5,
} as const;
export type ArenaEventType = ValueOf<typeof ARENA_EVENT>;

/** Death cause codes on the wire (ARENA_EVENT.DEATH, the DEATH arena trailer). 0 = none. */
export const ARENA_CAUSE_CODE = {
  NONE: 0,
  CUT: 1,
  ENCLOSED: 2,
  STARVED: 3,
  SELF: 4,
  EXIT_CAPTURED: 5,
} as const;
export type ArenaCauseCode = ValueOf<typeof ARENA_CAUSE_CODE>;

/** ARENA_EVENT.EXIT codes: how a life left with its bag paid. */
export const ARENA_EXIT_CODE = {
  CASH_OUT: 0,
  FORCED_IDLE: 1,
  FORCED_DISCONNECT: 2,
  SHUTDOWN: 3,
} as const;
export type ArenaExitCode = ValueOf<typeof ARENA_EXIT_CODE>;

/** DEATH / CASH_OUT_DONE arena trailer flag bits. */
export const ARENA_LIFE_FLAG = {
  /** A self-cut within 1 cell of the rim (S-8): results copy only, money is unchanged. */
  AT_WALL: 1 << 0,
  /** CASH_OUT_DONE of a forced exit (idle or disconnect autopilot; spec §9). */
  FORCED_EXIT: 1 << 1,
} as const;

/** OPTIONS flag bits (protocol §5.1). */
export const OPTIONS_FLAG = {
  WANT_MINIMAP_PLAYERS: 1 << 0,
  MENU_OPEN: 1 << 1,
} as const;

/** WELCOME flag bits (protocol §5.3). */
export const WELCOME_FLAG = {
  CUSTOM_SKINS_ENABLED: 1 << 0,
  MINIMAP_PLAYERS_ALLOWED: 1 << 1,
  SPECTATE_ALLOWED: 1 << 2,
  CUSTOM_SKINS_PUBLIC: 1 << 3,
  /** A money room: the WELCOME money block follows the base fields (protocol §5.3). */
  MONEY_ROOM: 1 << 4,
  /**
   * With MONEY_ROOM: a practice room (money-mode §1.4): the same rules with play money (silver
   * labels, the PRACTICE badge, no buy-in: PLAY carries no ticket).
   */
  PRACTICE_ROOM: 1 << 5,
} as const;

/** PLAY flag bits (protocol §5.1). Bits other than HAS_TICKET are reserved (0). */
export const PLAY_FLAG = {
  /** An entry ticket id (`str` ≤ 64 B) follows the flags byte (money rooms). */
  HAS_TICKET: 1 << 0,
} as const;

/** What a LEADERBOARD amount counts (protocol §5.4): floor(score) or lamports of value. */
export const AMOUNT_KIND = {
  SCORE: 0,
  LAMPORTS: 1,
} as const;
export type AmountKind = ValueOf<typeof AMOUNT_KIND>;

/** WELCOME money block `labelCurrency` (money-mode §7): the default unit of on-screen labels. */
export const LABEL_CURRENCY = {
  USD: 0,
  SOL: 1,
} as const;
export type LabelCurrencyCode = ValueOf<typeof LABEL_CURRENCY>;

/** PLAYERS record flag bits (protocol §5.4). */
export const PLAYER_FLAG = {
  HAS_SKIN: 1 << 0,
  PARTY_MEMBER: 1 << 1,
  IS_YOU: 1 << 2,
  TEAMMATE: 1 << 3,
  IS_BOT: 1 << 4,
} as const;

/** MOVE record mask bits (protocol §5.4). ABS and DELTA never appear together. */
export const MOVE_MASK = {
  ABS: 1 << 0,
  DELTA: 1 << 1,
  RADIUS: 1 << 2,
} as const;

/** LEADERBOARD entry flag bits (protocol §5.4). */
export const LEADERBOARD_FLAG = {
  IS_YOU: 1 << 0,
  PARTY_MEMBER: 1 << 1,
  IS_BOT: 1 << 2,
} as const;

/** MINIMAP entry flag bits (protocol §5.4). */
export const MINIMAP_FLAG = {
  IN_TOP20: 1 << 0,
  PARTY_MEMBER: 1 << 1,
} as const;

/** MATCH_RESULT standings flag bits (protocol §5.5). */
export const STANDING_FLAG = {
  IS_YOU: 1 << 0,
} as const;

/** NOTICE codes (protocol §5.6). */
export const NOTICE = {
  SPECTATE_FULL: 1,
  FEED_PAUSED: 2,
  FEED_RESUMED: 3,
  PARTY_JOINED: 4,
  PARTY_LEFT: 5,
  ROOM_CLOSING: 6,
  SERVER_RESTART: 7,
  NAME_REJECTED: 8,
  SKIN_REJECTED: 9,
  CANNOT_SPECTATE: 10,
  BR_QUEUED: 11,
  RESPAWN_IN: 12,
  /** Money rooms: CASH_OUT_START before money.minLifeSec; arg = whole seconds left (money-mode §4). */
  CASH_OUT_TOO_EARLY: 13,
  /** Money rooms: the cash-out completed but the payout is held for review (money-mode §9). */
  PAYOUT_DELAYED: 14,
  /**
   * Money rooms: this life will be frozen for inactivity soon (money-mode §5 idle rule); arg =
   * whole seconds left. Sent once per idle stretch, 15 s (at most half of `money.idleSec`) before the freeze.
   */
  IDLE_WARNING: 15,
  /**
   * Money rooms: this life was frozen for inactivity (it no longer steers and can be eaten); arg =
   * whole seconds until its automatic cash-out.
   */
  IDLE_FROZEN: 16,
} as const;
export type NoticeCode = ValueOf<typeof NOTICE>;

/** ERROR codes; the server closes with `4000 + code` right after (protocol §5.6). */
export const ERROR = {
  VERSION_MISMATCH: 1,
  ROOM_FULL: 2,
  RATE_LIMITED: 3,
  MALFORMED: 4,
  IDLE_TIMEOUT: 5,
  SHUTTING_DOWN: 6,
  ROOM_NOT_FOUND: 7,
  KICKED: 8,
  TOO_MANY_CONNECTIONS: 9,
  HANDSHAKE_TIMEOUT: 10,
  /** Money room PLAY without an entry ticket (money-mode §2.1). */
  TICKET_REQUIRED: 11,
  /** The ticket is unknown, expired or not usable here (money-mode §2.1). */
  TICKET_INVALID: 12,
  /** The ticket was already used by a spawn (money-mode §2.1 rule 4). */
  TICKET_USED: 13,
  /** The ticket's account already has a live money life in some room (money-mode §2.1 rule 5). */
  MONEY_LIFE_ACTIVE: 14,
  /** The ticket's stake is not this room's stake, or that stake is not offered (money-mode §1.3). */
  STAKE_NOT_OFFERED: 15,
  /**
   * Money rooms: the ticket belongs to another account than the one this socket is signed in as
   * (HELLO authToken), or the socket is not signed in at all. The ticket stays unused.
   */
  TICKET_NOT_YOURS: 16,
  /**
   * HELLO carried an authToken the server could not verify (expired, bad signature, another
   * app). The client gets a fresh token and reconnects, or reconnects without one (free play).
   */
  AUTH_INVALID: 17,
} as const;
/** Highest ERROR code in use. */
export const MAX_ERROR_CODE = 17;
export type ErrorCode = ValueOf<typeof ERROR>;

/** WebSocket close codes used by ball.io (protocol §1, §4, §5.6; ui-flow §9). */
export const CLOSE = {
  NORMAL: 1000,
  GOING_AWAY: 1001,
  ABNORMAL: 1006,
  MESSAGE_TOO_BIG: 1009,
  INTERNAL_ERROR: 1011,
  VERSION_MISMATCH: 4001,
  ROOM_FULL: 4002,
  RATE_LIMITED: 4003,
  MALFORMED: 4004,
  IDLE_TIMEOUT: 4005,
  SHUTTING_DOWN: 4006,
  ROOM_NOT_FOUND: 4007,
  KICKED: 4008,
  TOO_MANY_CONNECTIONS: 4009,
  HANDSHAKE_TIMEOUT: 4010,
  TICKET_REQUIRED: 4011,
  TICKET_INVALID: 4012,
  TICKET_USED: 4013,
  MONEY_LIFE_ACTIVE: 4014,
  STAKE_NOT_OFFERED: 4015,
  TICKET_NOT_YOURS: 4016,
  AUTH_INVALID: 4017,
} as const;
export type CloseCode = ValueOf<typeof CLOSE>;

/** The close code that follows an ERROR: `4000 + code`. */
export function closeCodeForError(code: ErrorCode): number {
  return 4000 + code;
}

/** The ERROR code behind a 40xx close code, or `undefined` for any other close code. */
export function errorCodeForClose(closeCode: number): ErrorCode | undefined {
  const code = closeCode - 4000;
  return code >= 1 && code <= MAX_ERROR_CODE ? (code as ErrorCode) : undefined;
}
